import React from 'react';
import styled from 'styled-components';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import DeleteIcon from '@mui/icons-material/Delete';
import Checkbox from '@mui/material/Checkbox';
import Autocomplete from '@mui/material/Autocomplete';
import Tooltip from '@mui/material/Tooltip';

const StyledTableHead = styled(TableHead)`
  background-color: #1976d2;
  
  & th {
    color: white;
    font-weight: bold;
  }
`;

// Memoized table component for better performance
const ConsumablesTable = React.memo(({ 
  items, 
  availableQuantity, 
  getRequestedQuantity, 
  handleToggleItem, 
  handleQuantityChange, 
  handleQuantityAdjust, 
  handlePartNumberChange,
  getPartNumberOptions,
  isSelectionView,
  handleSplitRow,
  handleRemoveSplitRow,
  getGroupTotalQuantity
}) => (
  <Table>
    <StyledTableHead>
      <TableRow>
        {isSelectionView && <TableCell>Select</TableCell>}
        <TableCell>Sl.No.</TableCell>
        <TableCell>Consumable</TableCell>
        <TableCell>Asset</TableCell>
        <TableCell>Asset Tag</TableCell>
        <TableCell>Part No</TableCell>
        <TableCell>Available Qty</TableCell>
        <TableCell>Requested Qty</TableCell>
        <TableCell>Issue Qty</TableCell>
        {isSelectionView && handleSplitRow && <TableCell>Actions</TableCell>}
      </TableRow>
    </StyledTableHead>
    <TableBody>
      {items.map((item, index) => {
        const availableQty = availableQuantity(item.partno);
        const isLowInventory = availableQty < parseInt(getRequestedQuantity(item));
        
        return (
          <TableRow key={index}>
            {isSelectionView && (
              <TableCell>
                <Checkbox 
                  checked={item.isSelected} 
                  onChange={() => handleToggleItem(index)}
                  disabled={item.partno && availableQty === 0}
                />
              </TableCell>
            )}
            <TableCell>{index + 1}</TableCell>
            <TableCell>{item.consumable || '-'}</TableCell>
            <TableCell>{item.asset || 'Not Applicable'}</TableCell>
            <TableCell>{item?.assettag?.label || 'Not Applicable'}</TableCell>
            <TableCell>
              {isSelectionView ? (
                <Autocomplete
                  size="small"
                  options={getPartNumberOptions ? getPartNumberOptions(item.consumable, item.asset, item.assettag, index) : []}
                  value={item.partno || null}
                  onChange={(event, newValue) => handlePartNumberChange && handlePartNumberChange(index, newValue)}
                  renderInput={(params) => <TextField {...params} label="Select Part No." />}
                  sx={{ minWidth: 150 }}
                  disabled={!item.isSelected}
                />
              ) : (
                item.partno || '-'
              )}
            </TableCell>
            <TableCell style={{ color: isLowInventory ? 'red' : 'inherit', fontWeight: isLowInventory ? 'bold' : 'normal' }}>
              {item.partno ? availableQty : '-'}
            </TableCell>
            <TableCell>{getRequestedQuantity(item) || '-'}</TableCell>
            <TableCell>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <IconButton 
                  size="small" 
                  onClick={() => handleQuantityAdjust(index, -1)}
                  disabled={isSelectionView ? (!item.isSelected || availableQty === 0 || !item.partno) : false}
                >
                  <RemoveIcon />
                </IconButton>
                
                <TextField
                  type="number"
                  size="small"
                  value={item.selectedQuantity}
                  onChange={(e) => handleQuantityChange(index, e.target.value)}
                  disabled={isSelectionView ? (!item.isSelected || availableQty === 0 || !item.partno) : false}
                  style={{ width: '60px', margin: '0 5px' }}
                  inputProps={{
                    min: 0,
                    max: getGroupTotalQuantity 
                      ? item.originalQuantity - getGroupTotalQuantity(item.groupId) + parseInt(item.selectedQuantity || 0, 10)
                      : Math.min(getRequestedQuantity(item), availableQty) 
                  }}
                />
                
                <IconButton 
                  size="small" 
                  onClick={() => handleQuantityAdjust(index, 1)}
                  disabled={isSelectionView ? (!item.isSelected || availableQty === 0 || !item.partno) : false}
                >
                  <AddIcon />
                </IconButton>
              </div>
            </TableCell>
            {isSelectionView && handleSplitRow && (
              <TableCell>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <Tooltip title="Split this row to assign different part numbers">
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => handleSplitRow(index)}
                      disabled={!item.isSelected}
                    >
                      <CallSplitIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  
                  {item.isSplit && (
                    <Tooltip title="Remove this split row">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleRemoveSplitRow(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </TableCell>
            )}
          </TableRow>
        );
      })}
    </TableBody>
  </Table>
));

ConsumablesTable.displayName = 'ConsumablesTable';

export default ConsumablesTable;
